import { db, runInTransaction } from '@/db/client';
import { reservas, reservaMovements, accounts } from '@/db/schema';
import { eq, and, isNull, desc, asc, sql, gte, lte, inArray } from 'drizzle-orm';
import { format } from 'date-fns';
import { generateId } from '@/lib/id';
import { roundMonetaryValue } from '@/lib/formatting';
import { ExpectedError, HandledByCallerError } from '@/lib/errorUtils';
import i18n from '@/lib/i18n';

// Executor = db OU a transação (tx) — pra reusar as leituras de saldo/reservado dentro e
// fora de transação sem duplicar o SQL.
type Executor = typeof db | Parameters<Parameters<typeof db.transaction>[0]>[0];

/** Total reservado (ativo) numa conta. Aceita db ou tx. */
async function sumReservedInAccount(exec: Executor, accountId: string): Promise<number> {
    const [row] = await exec
        .select({ total: sql<number>`COALESCE(SUM(${reservas.currentAmount}), 0)` })
        .from(reservas)
        .where(and(
            eq(reservas.accountId, accountId),
            eq(reservas.isArchived, 0),
            isNull(reservas.deletedAt),
        ));
    return roundMonetaryValue(row?.total ?? 0);
}

/** Saldo (currentBalance) da conta. Aceita db ou tx. */
async function balanceOfAccount(exec: Executor, accountId: string): Promise<number> {
    const [acct] = await exec
        .select({ balance: accounts.currentBalance })
        .from(accounts)
        .where(eq(accounts.id, accountId));
    return acct?.balance ?? 0;
}

/**
 * Disponível (FRESCO) da conta: saldo − reservado, nunca negativo. Leitura pura — o
 * ADR 0017 permite ler o saldo pra clampar/avisar (a Reserva segue sem ESCREVER em conta).
 * Fonte única do Disponível: as telas e o top-up leem daqui em vez de recalcular a fórmula
 * a partir do cache do React Query (o que abria espaço pra clobber com valor stale).
 */
export async function getAvailableByAccount(accountId: string): Promise<number> {
    const balance = await balanceOfAccount(db, accountId);
    const reserved = await sumReservedInAccount(db, accountId);
    return Math.max(0, roundMonetaryValue(balance - reserved));
}

/**
 * Disponível BRUTO (pode ser NEGATIVO): saldo − reservado, sem clampar em zero. Leitura pura.
 * Usado pra dimensionar o aporte "à parte" (ADR 0021): quando a conta está negativa, creditar só
 * o valor pedido deixaria o negativo "comer" parte dele e a caixinha reservaria menos. Sabendo o
 * bruto, o aporte credita `valor + déficit` e a caixinha reserva o valor cheio.
 */
export async function getRawAvailableByAccount(accountId: string): Promise<number> {
    const balance = await balanceOfAccount(db, accountId);
    const reserved = await sumReservedInAccount(db, accountId);
    return roundMonetaryValue(balance - reserved);
}

// Reservas (caixinhas): dinheiro reservado DENTRO de uma conta. Guardar/resgatar é um
// movimento NEUTRO — não cria transação, não muda o currentBalance da conta nem o
// patrimônio. Só desloca valor entre Disponível e Reservado na mesma conta. Por isso
// este repositório NÃO escreve em transactions nem em accounts. Ver ADR 0015.
// (Guardar só LÊ o saldo da conta — leitura pura — pra clampar no Disponível.)

// ─── Types ───────────────────────────────────────────────────

export type Reserva = typeof reservas.$inferSelect;
export type NewReserva = typeof reservas.$inferInsert;
export type ReservaMovement = typeof reservaMovements.$inferSelect;

export interface ReservaWithAccount extends Reserva {
    accountName?: string | null;
    accountColor?: string | null;
    accountIcon?: string | null;
    /** 1 quando a conta foi arquivada ou excluída (só em `getReservaById`; ADR 0162). */
    accountInactive?: number;
}

export interface ReservaSummary {
    totalReservas: number;
    totalReserved: number;
}

const reservaColumns = {
    id: reservas.id,
    accountId: reservas.accountId,
    name: reservas.name,
    currentAmount: reservas.currentAmount,
    targetAmount: reservas.targetAmount,
    deadline: reservas.deadline,
    icon: reservas.icon,
    color: reservas.color,
    creditCardId: reservas.creditCardId,
    isEmergencyFund: reservas.isEmergencyFund,
    isArchived: reservas.isArchived,
    sortOrder: reservas.sortOrder,
    createdAt: reservas.createdAt,
    updatedAt: reservas.updatedAt,
    deletedAt: reservas.deletedAt,
};

// ─── Read ────────────────────────────────────────────────────

/**
 * A caixinha só existe enquanto a conta dela está de pé (ADR 0162). Conta arquivada ou excluída
 * tira as caixinhas de todas as leituras de conjunto (lista, Home, atalhos, limite garantido,
 * contagem do plano) SEM mexer nelas: se a conta voltar, elas voltam como estavam. Antes seguiam
 * ativas, abatendo um Disponível que não existia mais e aceitando rendimento numa conta morta.
 */
const ACTIVE_ACCOUNT = [isNull(accounts.deletedAt), eq(accounts.isArchived, 0)];

export async function getReservas(includeArchived = false): Promise<ReservaWithAccount[]> {
    const conditions = [isNull(reservas.deletedAt), ...ACTIVE_ACCOUNT];
    if (!includeArchived) {
        conditions.push(eq(reservas.isArchived, 0));
    }

    return db
        .select({
            ...reservaColumns,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
        })
        .from(reservas)
        .innerJoin(accounts, eq(reservas.accountId, accounts.id))
        .where(and(...conditions))
        .orderBy(asc(reservas.sortOrder), desc(reservas.createdAt));
}

export async function getReservaById(id: string): Promise<ReservaWithAccount | null> {
    const rows = await db
        .select({
            ...reservaColumns,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
            // O detalhe abre por link do extrato mesmo com a conta fora: aí fica só leitura.
            accountInactive: sql<number>`CASE WHEN ${accounts.id} IS NULL OR ${accounts.deletedAt} IS NOT NULL OR ${accounts.isArchived} = 1 THEN 1 ELSE 0 END`,
        })
        .from(reservas)
        .leftJoin(accounts, eq(reservas.accountId, accounts.id))
        .where(eq(reservas.id, id));

    return rows[0] ?? null;
}

export async function getReservasByAccount(accountId: string): Promise<Reserva[]> {
    return db
        .select()
        .from(reservas)
        .where(and(
            eq(reservas.accountId, accountId),
            eq(reservas.isArchived, 0),
            isNull(reservas.deletedAt),
        ))
        .orderBy(asc(reservas.sortOrder), desc(reservas.createdAt));
}

/** Total reservado (ativo) numa conta — é o que abate o Disponível dela. */
export async function getReservedAmountByAccount(accountId: string): Promise<number> {
    return sumReservedInAccount(db, accountId);
}

/** Reservado por conta (mapa accountId → total) — pra calcular o Disponível de várias contas de uma vez. */
export async function getReservedByAccount(): Promise<Record<string, number>> {
    const rows = await db
        .select({
            accountId: reservas.accountId,
            total: sql<number>`COALESCE(SUM(${reservas.currentAmount}), 0)`,
        })
        .from(reservas)
        .innerJoin(accounts, eq(reservas.accountId, accounts.id))
        .where(and(eq(reservas.isArchived, 0), isNull(reservas.deletedAt), ...ACTIVE_ACCOUNT))
        .groupBy(reservas.accountId);

    const map: Record<string, number> = {};
    for (const row of rows) {
        if (row.accountId) map[row.accountId] = roundMonetaryValue(row.total);
    }
    return map;
}

export async function getReservaMovements(reservaId: string): Promise<ReservaMovement[]> {
    return db
        .select()
        .from(reservaMovements)
        .where(eq(reservaMovements.reservaId, reservaId))
        .orderBy(desc(reservaMovements.date), desc(reservaMovements.createdAt));
}

export interface ReservaMovementWithReserva extends ReservaMovement {
    reservaName: string;
    reservaColor: string;
    reservaIcon: string;
    accountName: string | null;
}

/**
 * Movimentos de guardar/resgatar das caixinhas no período — pra EXIBIR no extrato
 * (conta e Transações) como linha informativa (preferência showReservaMovementsInStatement).
 * Movimento é neutro (ADR 0015): isto NÃO vira transação nem entra em totais.
 * `accountScope` opcional recorta por conta: uma (extrato da conta) ou várias (filtro de
 * contas da aba Transações, ADR 0170); lista vazia = todas.
 * Inclui caixinhas arquivadas (histórico é histórico), exclui as excluídas. 'yield' fica
 * de fora: o rendimento já credita a conta como receita (creditAccountYield) — mostrar o
 * movimento duplicaria a linha.
 */
export async function getReservaMovementsInPeriod(
    startDate: string,
    endDate: string,
    accountScope?: string | readonly string[],
): Promise<ReservaMovementWithReserva[]> {
    const accountIds = (typeof accountScope === 'string' ? [accountScope] : [...(accountScope ?? [])])
        .filter(Boolean);
    return db
        .select({
            id: reservaMovements.id,
            reservaId: reservaMovements.reservaId,
            type: reservaMovements.type,
            amount: reservaMovements.amount,
            date: reservaMovements.date,
            notes: reservaMovements.notes,
            transactionId: reservaMovements.transactionId,
            createdAt: reservaMovements.createdAt,
            reservaName: reservas.name,
            reservaColor: reservas.color,
            reservaIcon: reservas.icon,
            accountName: accounts.name,
        })
        .from(reservaMovements)
        .innerJoin(reservas, eq(reservaMovements.reservaId, reservas.id))
        .leftJoin(accounts, eq(reservas.accountId, accounts.id))
        .where(and(
            ...(accountIds.length > 0 ? [inArray(reservas.accountId, accountIds)] : []),
            isNull(reservas.deletedAt),
            inArray(reservaMovements.type, ['deposit', 'withdraw']),
            gte(reservaMovements.date, startDate),
            lte(reservaMovements.date, endDate),
        ))
        .orderBy(asc(reservaMovements.date), asc(reservaMovements.createdAt));
}

/**
 * A caixinha ATIVA que recebeu o rendimento desta receita (ADR 0161), ou null. A tela da
 * transação mostra "Rendimento da caixinha X" e esconde excluir/converter — isso se desfaz pela
 * caixinha. Caixinha excluída ou arquivada devolve null: a receita volta a ser comum.
 */
export async function getYieldReservaForTransaction(
    transactionId: string,
): Promise<{ reservaId: string; reservaName: string } | null> {
    const [row] = await db
        .select({ reservaId: reservas.id, reservaName: reservas.name })
        .from(reservaMovements)
        .innerJoin(reservas, eq(reservaMovements.reservaId, reservas.id))
        // Mesmo critério do bloqueio em transactionRepository: conta fora solta a receita.
        .innerJoin(accounts, eq(reservas.accountId, accounts.id))
        .where(and(
            eq(reservaMovements.transactionId, transactionId),
            eq(reservaMovements.type, 'yield'),
            isNull(reservas.deletedAt),
            eq(reservas.isArchived, 0),
            ...ACTIVE_ACCOUNT,
        ))
        .limit(1);
    return row ?? null;
}

export interface ReservaPeriodSummary {
    /** Guardado no período (deposit). */
    deposited: number;
    /** Resgatado no período (withdraw). */
    withdrawn: number;
    /** Rendimento no período (yield) — também é uma receita da conta. */
    yielded: number;
    /** Reservado nas caixinhas ativas da conta no fim do período, pelos movimentos datados. */
    reservedAtEnd: number;
    /** A conta tem (ou teve no período) alguma caixinha — sem nada, o bloco não aparece. */
    hasReservas: boolean;
}

/**
 * O bloco "Caixinhas" do relatório da conta (P5 da análise do e-mail do Denis, 27/09/2026):
 * o que explica o Disponível mudar sem Entrada nem Saída. Movimentos de caixinhas não excluídas
 * da conta; o reservado do fim do período segue a conta do extrato (só as ativas, arquivar não
 * é datado).
 */
export async function getReservaPeriodSummary(
    accountId: string,
    startDate: string,
    endDate: string,
): Promise<ReservaPeriodSummary> {
    const rows = await db
        .select({
            type: reservaMovements.type,
            amount: reservaMovements.amount,
            date: reservaMovements.date,
            isArchived: reservas.isArchived,
        })
        .from(reservaMovements)
        .innerJoin(reservas, eq(reservaMovements.reservaId, reservas.id))
        .where(and(
            eq(reservas.accountId, accountId),
            isNull(reservas.deletedAt),
            lte(reservaMovements.date, endDate),
        ));
    const [countRow] = await db
        .select({ count: sql<number>`COUNT(*)` })
        .from(reservas)
        .where(and(eq(reservas.accountId, accountId), isNull(reservas.deletedAt), eq(reservas.isArchived, 0)));

    let deposited = 0;
    let withdrawn = 0;
    let yielded = 0;
    let reservedAtEnd = 0;
    let movedInPeriod = false;
    for (const row of rows) {
        const signed = row.type === 'withdraw' ? -row.amount : row.amount;
        if (row.isArchived === 0) reservedAtEnd += signed;
        if (row.date < startDate) continue;
        movedInPeriod = true;
        if (row.type === 'deposit') deposited += row.amount;
        else if (row.type === 'withdraw') withdrawn += row.amount;
        else if (row.type === 'yield') yielded += row.amount;
    }
    return {
        deposited: roundMonetaryValue(deposited),
        withdrawn: roundMonetaryValue(withdrawn),
        yielded: roundMonetaryValue(yielded),
        reservedAtEnd: roundMonetaryValue(reservedAtEnd),
        hasReservas: movedInPeriod || (countRow?.count ?? 0) > 0,
    };
}

export async function getReservaSummary(): Promise<ReservaSummary> {
    const [row] = await db
        .select({
            count: sql<number>`COUNT(*)`,
            total: sql<number>`COALESCE(SUM(${reservas.currentAmount}), 0)`,
        })
        .from(reservas)
        .innerJoin(accounts, eq(reservas.accountId, accounts.id))
        .where(and(eq(reservas.isArchived, 0), isNull(reservas.deletedAt), ...ACTIVE_ACCOUNT));

    return {
        totalReservas: row?.count ?? 0,
        totalReserved: roundMonetaryValue(row?.total ?? 0),
    };
}

// ─── Write ───────────────────────────────────────────────────

export interface CreateReservaInput {
    accountId: string;
    name: string;
    targetAmount?: number | null;
    deadline?: string | null;
    icon?: string;
    color?: string;
    creditCardId?: string | null;
    /** Marcador de reserva de emergência (ADR 0022): conta no sub-score "Reserva" da saúde. */
    isEmergencyFund?: boolean;
    /** Valor já guardado na criação (vira um movimento inicial de guardar). */
    initialAmount?: number;
    /** Parte do inicial que acabou de entrar na conta ("à parte", ADR 0021): cabe inteira (ADR 0167). */
    initialIncoming?: number;
}

export async function createReserva(input: CreateReservaInput): Promise<Reserva> {
    return runInTransaction(() => insertReserva(input));
}

/** Corpo do createReserva, sem abrir transação: para quem já está numa (reservaAccountFlows). */
export async function insertReserva(input: CreateReservaInput): Promise<Reserva> {
    const id = generateId();
    const now = new Date().toISOString();
    const requested = input.initialAmount && input.initialAmount > 0
        ? roundMonetaryValue(input.initialAmount)
        : 0;
    const tx = db;

    // "Já guardar agora" no cadastro: clampa no Disponível da conta, igual ao guardar
    // (não dá pra reservar mais do que está livre). O top-up fica pro Guardar na caixinha.
    let initial = 0;
    if (requested > 0) {
        const available = roundMonetaryValue(
            await balanceOfAccount(tx, input.accountId) - await sumReservedInAccount(tx, input.accountId),
        );
        initial = Math.min(requested, roomToReserve(available, Math.max(0, input.initialIncoming ?? 0)));
    }

    await tx.insert(reservas).values({
        id,
        accountId: input.accountId,
        name: input.name,
        currentAmount: initial,
        targetAmount: input.targetAmount ?? null,
        deadline: input.deadline ?? null,
        icon: input.icon ?? 'piggy-bank',
        color: input.color ?? '#10B981',
        creditCardId: input.creditCardId ?? null,
        isEmergencyFund: input.isEmergencyFund ? 1 : 0,
        createdAt: now,
        updatedAt: now,
    });

    if (initial > 0) {
        await tx.insert(reservaMovements).values({
            id: generateId(),
            reservaId: id,
            type: 'deposit',
            amount: initial,
            date: format(new Date(), 'yyyy-MM-dd'), // dia LOCAL (não UTC)
            createdAt: now,
        });
    }

    const [row] = await tx.select().from(reservas).where(eq(reservas.id, id));
    return row;
}

// accountId de propósito FORA: a conta é imutável depois de criada — trocar deslocaria
// o reservado entre contas sem movimento e sem mexer no saldo. Pra mudar de conta,
// exclui e cria de novo. Ver ADR 0015 / docs/features/RESERVAS.md.
export interface UpdateReservaInput {
    name?: string;
    targetAmount?: number | null;
    deadline?: string | null;
    icon?: string;
    color?: string;
    creditCardId?: string | null;
    isEmergencyFund?: boolean;
}

export async function updateReserva(id: string, input: UpdateReservaInput): Promise<void> {
    const { isEmergencyFund, ...rest } = input;
    await db
        .update(reservas)
        .set({
            ...rest,
            ...(isEmergencyFund !== undefined ? { isEmergencyFund: isEmergencyFund ? 1 : 0 } : {}),
            updatedAt: new Date().toISOString(),
        })
        .where(eq(reservas.id, id));
}

/** Limite garantido: soma do valor guardado nas caixinhas ativas vinculadas ao cartão
 *  (`creditCardId`). Esse total SOMA ao limite disponível do cartão — espelha o
 *  Nu Limite Garantido/PicPay, onde o dinheiro reservado banca limite extra. */
export async function getGuaranteedLimitByCard(cardId: string): Promise<number> {
    const [row] = await db
        .select({ total: sql<number>`COALESCE(SUM(${reservas.currentAmount}), 0)` })
        .from(reservas)
        .innerJoin(accounts, eq(reservas.accountId, accounts.id))
        .where(and(
            eq(reservas.creditCardId, cardId),
            eq(reservas.isArchived, 0),
            isNull(reservas.deletedAt),
            ...ACTIVE_ACCOUNT,
        ));
    return roundMonetaryValue(row?.total ?? 0);
}

/** Exclui a reserva (soft). O dinheiro reservado volta pro Disponível automaticamente
 *  (a reserva sai do somatório), sem criar transação — o valor nunca saiu da conta. */
export async function deleteReserva(id: string): Promise<void> {
    await db
        .update(reservas)
        .set({ deletedAt: new Date().toISOString() })
        .where(eq(reservas.id, id));
}

/**
 * Erro de reativação bloqueada: o valor congelado da caixinha não cabe mais no Disponível
 * da conta. Carrega os números pra UI montar as duas saídas (ADR 0019):
 *  - `shortfall`: quanto falta na conta pra caber o congelado (= o Ajuste de saldo a creditar);
 *  - `frozen`: o valor congelado (quanto a caixinha quer re-reservar);
 *  - `available`: o Disponível livre da conta agora (≥ 0, pra "reativar com o que cabe").
 */
export class ReservaReactivationBlockedError extends HandledByCallerError {
    readonly code = 'RESERVA_REACTIVATION_BLOCKED';
    constructor(
        readonly shortfall: number,
        readonly frozen: number,
        readonly available: number,
    ) {
        super('A conta não tem saldo livre pra reativar com o valor guardado.');
        this.name = 'ReservaReactivationBlockedError';
    }
}

export function isReservaReactivationBlocked(e: unknown): e is ReservaReactivationBlockedError {
    return e instanceof ReservaReactivationBlockedError;
}

/**
 * Arquivar: tira a caixinha da lista ativa SEM apagar. O valor reservado volta pro
 * Disponível da conta automaticamente — `sumReservedInAccount`/`getReservedByAccount`
 * filtram `isArchived = 0`, então a caixinha sai do somatório. O `currentAmount` fica
 * CONGELADO no registro e o histórico de Movimentos é preservado. Sem transação: o
 * dinheiro nunca saiu da conta (overlay puro — ADR 0015/0019).
 */
export async function archiveReserva(id: string): Promise<void> {
    await db
        .update(reservas)
        .set({ isArchived: 1, updatedAt: new Date().toISOString() })
        .where(eq(reservas.id, id));
}

/**
 * Reativar: re-reserva o valor congelado. Como arquivar soltou o dinheiro, reativar precisa
 * caber no Disponível da conta. BLOQUEIA (lança `ReservaReactivationBlockedError`) quando o
 * congelado passa do que está livre — mesma invariante `Σ(reservas ativas) ≤ saldo` da
 * exclusão de Movimento. A caixinha arquivada não conta no `sumReservedInAccount` (ainda
 * `isArchived = 1`), então comparamos `congelado` contra `saldo − reservado_ativo`. ADR 0019.
 */
export async function unarchiveReserva(id: string): Promise<void> {
    return runInTransaction(async () => {
        const tx = db;
        const [reserva] = await tx.select().from(reservas).where(eq(reservas.id, id));
        if (!reserva) return;

        const frozen = roundMonetaryValue(reserva.currentAmount);
        if (frozen > 0) {
            const balance = await balanceOfAccount(tx, reserva.accountId);
            const reservedActive = await sumReservedInAccount(tx, reserva.accountId);
            const available = roundMonetaryValue(balance - reservedActive);
            if (frozen > available + 1e-6) {
                throw new ReservaReactivationBlockedError(
                    roundMonetaryValue(frozen - available), // shortfall (Ajuste a creditar)
                    frozen,
                    Math.max(0, available),
                );
            }
        }

        await tx
            .update(reservas)
            .set({ isArchived: 0, updatedAt: new Date().toISOString() })
            .where(eq(reservas.id, id));
    });
}

/**
 * Reativar com o que cabe: encolhe o congelado pro Disponível livre da conta e registra um
 * Resgate de ajuste (`withdraw`) da diferença, mantendo `currentAmount = Σ(movimentos)`. Se
 * não couber nada, cai pra R$ 0 ("zerada"). Retorna o valor efetivamente reativado. ADR 0019.
 */
export async function unarchiveReservaClamped(id: string): Promise<number> {
    return runInTransaction(async () => {
        const tx = db;
        const [reserva] = await tx.select().from(reservas).where(eq(reservas.id, id));
        if (!reserva) return 0;

        const balance = await balanceOfAccount(tx, reserva.accountId);
        const reservedActive = await sumReservedInAccount(tx, reserva.accountId);
        const available = Math.max(0, roundMonetaryValue(balance - reservedActive));
        const fits = roundMonetaryValue(Math.min(reserva.currentAmount, available));
        const drop = roundMonetaryValue(reserva.currentAmount - fits);
        const now = new Date().toISOString();

        if (drop > 0) {
            await tx.insert(reservaMovements).values({
                id: generateId(),
                reservaId: id,
                type: 'withdraw',
                amount: drop,
                date: format(new Date(), 'yyyy-MM-dd'), // dia LOCAL (não UTC)
                notes: 'Ajuste na reativação',
                createdAt: now,
            });
        }

        await tx
            .update(reservas)
            .set({ currentAmount: fits, isArchived: 0, updatedAt: now })
            .where(eq(reservas.id, id));

        return fits;
    });
}

/**
 * Guardar: move do Disponível para o Reservado na mesma conta. Movimento neutro.
 * Lê e escreve dentro de UMA transação pra que o saque e a atualização do saldo
 * sejam atômicos — dois "guardar" simultâneos não se sobrescrevem (a transação
 * serializa o read-modify-write). Arredonda em centavos pra não acumular drift de
 * float. Clampa no Disponível da conta (saldo − já reservado): não dá pra reservar
 * mais do que existe. Retorna o valor efetivamente guardado (0 = nada aconteceu).
 */
/**
 * Quanto cabe guardar agora. Do Disponível, só o que está livre (nunca abaixo de zero).
 * Dinheiro que ACABOU de entrar na conta para isto (`incoming`: dinheiro novo, transferência,
 * ajuste do aporte) cabe inteiro, e o Disponível volta a ser o de antes da entrada, mesmo
 * negativo. Cobrir o negativo com esse dinheiro exigia creditar um valor que não entrou e tirava
 * o saldo do app do saldo do banco (ADR 0167).
 */
function roomToReserve(available: number, incoming: number): number {
    const before = roundMonetaryValue(available - incoming);
    return roundMonetaryValue(incoming + Math.max(0, before));
}

export async function guardar(
    reservaId: string,
    amount: number,
    date: string,
    notes?: string,
    options?: { incoming?: number },
): Promise<number> {
    const amt = roundMonetaryValue(amount);
    if (amt <= 0) return 0;

    return runInTransaction(async () => {
        const tx = db;
        const [reserva] = await tx.select().from(reservas).where(eq(reservas.id, reservaId));
        if (!reserva) return 0;

        // Disponível da conta = saldo − tudo que já está reservado nela (inclui esta
        // reserva). Clampar aqui é a fonte da verdade; ler o saldo é leitura pura e não
        // fere o overlay neutro do ADR 0015. Ver docs/features/RESERVAS.md.
        const available = roundMonetaryValue(
            await balanceOfAccount(tx, reserva.accountId) - await sumReservedInAccount(tx, reserva.accountId),
        );
        const deposit = Math.min(amt, roomToReserve(available, Math.max(0, options?.incoming ?? 0)));
        if (deposit <= 0) return 0;

        const now = new Date().toISOString();
        await tx.insert(reservaMovements).values({
            id: generateId(),
            reservaId,
            type: 'deposit',
            amount: deposit,
            date,
            notes: notes ?? null,
            createdAt: now,
        });

        const newAmount = roundMonetaryValue(reserva.currentAmount + deposit);
        await tx
            .update(reservas)
            .set({ currentAmount: newAmount, updatedAt: now })
            .where(eq(reservas.id, reservaId));

        return deposit;
    });
}

/**
 * Resgatar: devolve do Reservado para o Disponível. Não deixa o saldo da reserva
 * negativo (clampa no que tem). Atômico e arredondado como o guardar. Retorna o
 * valor efetivamente resgatado (0 = reserva sumiu ou já estava zerada) — o caller
 * usa isso pra não anunciar sucesso num no-op.
 */
export async function resgatar(
    reservaId: string,
    amount: number,
    date: string,
    notes?: string,
): Promise<number> {
    const amt = roundMonetaryValue(amount);
    if (amt <= 0) return 0;

    return runInTransaction(async () => {
        const tx = db;
        const [reserva] = await tx.select().from(reservas).where(eq(reservas.id, reservaId));
        if (!reserva) return 0;

        const withdraw = Math.min(amt, reserva.currentAmount);
        if (withdraw <= 0) return 0;

        const now = new Date().toISOString();
        await tx.insert(reservaMovements).values({
            id: generateId(),
            reservaId,
            type: 'withdraw',
            amount: withdraw,
            date,
            notes: notes ?? null,
            createdAt: now,
        });

        const newAmount = roundMonetaryValue(reserva.currentAmount - withdraw);
        await tx
            .update(reservas)
            .set({ currentAmount: newAmount, updatedAt: now })
            .where(eq(reservas.id, reservaId));

        return withdraw;
    });
}

/**
 * Rendimento: dinheiro NOVO que a caixinha rendeu (juros/CDI). Diferente do Guardar (que só
 * desloca do Disponível pro Reservado), o rendimento ENTRA na conta — quem credita o saldo como
 * RECEITA é o fluxo de conta (ver useRegistrarRendimento). Aqui só registramos o movimento de
 * rendimento e somamos ao reservado: como o valor foi creditado na conta no MESMO fluxo, o
 * Disponível não muda e a invariante Σ(reservas) ≤ saldo se mantém. Não clampa (o dinheiro acabou
 * de entrar). Atômico e arredondado como guardar/resgatar. Retorna o valor registrado (0 = no-op).
 */
export async function registrarRendimento(
    reservaId: string,
    amount: number,
    date: string,
    notes?: string,
    /** A receita que o fluxo creditou na conta (ADR 0161): os dois se desfazem juntos. */
    transactionId?: string,
): Promise<number> {
    const amt = roundMonetaryValue(amount);
    if (amt <= 0) return 0;

    return runInTransaction(async () => {
        const tx = db;
        const [reserva] = await tx.select().from(reservas).where(eq(reservas.id, reservaId));
        // Excluída ou arquivada não rende: a receita ligada ficaria sem caixinha ativa (ADR 0161).
        if (!reserva || reserva.deletedAt || reserva.isArchived === 1) return 0;

        const now = new Date().toISOString();
        await tx.insert(reservaMovements).values({
            id: generateId(),
            reservaId,
            type: 'yield',
            amount: amt,
            date,
            notes: notes ?? null,
            transactionId: transactionId ?? null,
            createdAt: now,
        });

        const newAmount = roundMonetaryValue(reserva.currentAmount + amt);
        await tx
            .update(reservas)
            .set({ currentAmount: newAmount, updatedAt: now })
            .where(eq(reservas.id, reservaId));

        return amt;
    });
}

/**
 * Exclui um Movimento (uma entrada do histórico) e REVERTE seu efeito no reservado:
 * apagar um `deposit` (Guardar) tira do `currentAmount`; apagar um `withdraw` (Resgatar)
 * devolve. Mantém sempre `currentAmount = Σ(movimentos)`.
 *
 * BLOQUEIA (lança) quando reverter quebraria uma invariante, em vez de clampar e
 * dessincronizar o número do histórico (decisão do grill 2026-06-25):
 *  - apagar um Guardar cujo dinheiro já foi resgatado depois deixaria o reservado < 0;
 *  - apagar um Resgatar re-reserva o valor e não pode estourar o saldo da conta
 *    (Disponível < 0).
 * Não toca em transação nenhuma — se o Guardar veio com um Ajuste de saldo na conta
 * (ADR 0017), o Ajuste é fato à parte e continua no extrato. Hard-delete (a tabela de
 * movimentos não tem soft-delete).
 *
 * Devolve a receita ligada a um rendimento (ADR 0161), pra quem chama apagá-la junto: este
 * repositório não escreve em transações (ADR 0015). `null` = nada a apagar.
 */
export async function deleteReservaMovement(movementId: string): Promise<string | null> {
    return runInTransaction(async () => {
        const tx = db;
        const [mov] = await tx.select().from(reservaMovements).where(eq(reservaMovements.id, movementId));
        if (!mov) return null;

        const [reserva] = await tx.select().from(reservas).where(eq(reservas.id, mov.reservaId));
        if (!reserva) return null;

        // Reverter o movimento: deposit/yield (+) saem do reservado; withdraw (−) volta pro
        // reservado. O rendimento ligado leva a receita junto (quem apaga é o caller); o Ajuste
        // de saldo do "Completar e reservar" continua fato à parte.
        const delta = mov.type === 'withdraw' ? mov.amount : -mov.amount;
        const newAmount = roundMonetaryValue(reserva.currentAmount + delta);

        if (newAmount < 0) {
            throw new ExpectedError(i18n.t('ui:errors.savedAmountWithdrawnLater'));
        }

        // Reverter um Resgatar re-reserva valor: não pode passar do saldo da conta.
        if (delta > 0) {
            const balance = await balanceOfAccount(tx, reserva.accountId);
            const reservedTotal = await sumReservedInAccount(tx, reserva.accountId);
            // Troca a contribuição desta reserva (atual → nova) no total reservado da conta.
            const reservedAfter = roundMonetaryValue(reservedTotal - reserva.currentAmount + newAmount);
            if (reservedAfter > roundMonetaryValue(balance) + 1e-6) {
                throw new ExpectedError(i18n.t('ui:errors.noFreeBalanceToReReserve'));
            }
        }

        await tx.delete(reservaMovements).where(eq(reservaMovements.id, movementId));
        await tx
            .update(reservas)
            .set({ currentAmount: newAmount, updatedAt: new Date().toISOString() })
            .where(eq(reservas.id, reserva.id));

        return mov.type === 'yield' ? mov.transactionId : null;
    });
}

/**
 * Sync (ADR 0163): refaz o valor das caixinhas a partir dos movimentos (guardado + rendimento −
 * resgatado), a mesma soma que o histórico e o reservado de fim de mês já usam. O valor
 * guardado na linha é uma célula só, e o last-write-wins do sync fica com o de UM aparelho
 * quando os dois mexeram na caixinha antes de se verem; guardar e resgatar somam em cima dele,
 * então o erro não se desfazia sozinho.
 *
 * Nunca abaixo de zero: dois resgates em aparelhos diferentes podem, juntos, passar do que
 * havia, e caixinha negativa aumentaria o Disponível. Grava só onde diverge, para não ecoar
 * entre os aparelhos. Devolve quantas caixinhas corrigiu.
 */
export async function reconcileReservaAmounts(reservaIds: Iterable<string>): Promise<number> {
    let fixed = 0;
    for (const id of new Set(reservaIds)) {
        const [row] = await db
            .select({
                currentAmount: reservas.currentAmount,
                // Pelo parâmetro, não por `${reservas.id}`: o drizzle escreve a coluna sem a
                // tabela, e dentro da subconsulta `id` vira o do movimento (soma sempre zero).
                history: sql<number>`COALESCE((
                    SELECT SUM(CASE
                        WHEN m.type = 'withdraw' THEN -m.amount
                        WHEN m.type IN ('deposit', 'yield') THEN m.amount
                        ELSE 0
                    END)
                    FROM reserva_movements m
                    WHERE m.reserva_id = ${id}
                ), 0)`,
            })
            .from(reservas)
            .where(eq(reservas.id, id));
        if (!row) continue;

        const amount = Math.max(0, roundMonetaryValue(row.history));
        if (Math.round(amount * 100) === Math.round(row.currentAmount * 100)) continue;
        await db.update(reservas).set({ currentAmount: amount }).where(eq(reservas.id, id));
        fixed += 1;
    }
    return fixed;
}
